<script setup lang="ts">
import type { Company } from "@asap/shared/schemas/company";
import { useCompanyDetailsRemuneration } from "~/components/company/composables/useCompanyDetailsRemuneration";

const props = defineProps<{
  company: Company;
}>();

const canEditRemuneration = true;

const {
  referenceDocumentIds,
  referenceDocumentsLoading,
  remunerationTemplates,
  templatesLoading,
  isConsultantOrganizationMissing,
  remunerationSettings,
  remunerationSettingsLoading,
  showCreateModal,
  editTemplate,
  duplicateDraft,
  deleteTemplate,
  showNightHoursModal,
  selectedOrganizationId,
  openCreateModal,
  openEditModal,
  openDuplicateModal,
  openDeleteModal,
  onTemplateModalClose,
  onTemplateSaved,
  onTemplateDeleted,
  onSettingsUpdated,
  onReferenceDocumentUpload,
} = useCompanyDetailsRemuneration(() => props.company);
</script>

<template>
  <div>
    <div class="company-details-section-title">
      <AwIcon name="i-ph-building-duotone" />
      {{ $t("companies.remuneration.title") }}
    </div>
    <div style="background-color: var(--gray-5); height: 1px; width: 100%; margin-top: 10px"></div>

    <RemunerationReferenceDocument
      :reference-document-ids="referenceDocumentIds"
      :company-siren="company.siren ?? ''"
      :loading="referenceDocumentsLoading"
      :company-id="company.id"
      @upload="onReferenceDocumentUpload"
    />

    <RemunerationTemplatesList
      :templates="remunerationTemplates"
      :loading="templatesLoading"
      :missing-organization="isConsultantOrganizationMissing"
      :can-edit="canEditRemuneration"
      @create="openCreateModal"
      @edit="openEditModal"
      @duplicate="openDuplicateModal"
      @delete="openDeleteModal"
    />

    <div class="night-hours-section">
      <RemunerationNightHoursDisplay
        :settings="remunerationSettings"
        :loading="remunerationSettingsLoading"
        :can-edit="canEditRemuneration"
        @edit="showNightHoursModal = true"
      />
    </div>

    <LazyRemunerationTemplateCreationModal
      v-if="(showCreateModal || editTemplate || duplicateDraft) && selectedOrganizationId"
      :company-id="company.id"
      :organization-id="editTemplate?.organization_id ?? selectedOrganizationId"
      :template="editTemplate"
      :duplicate-draft="duplicateDraft"
      :reference-document-ids="referenceDocumentIds"
      @close="onTemplateModalClose"
      @saved="onTemplateSaved"
    />
    <LazyRemunerationDeleteModal
      v-if="deleteTemplate"
      :template="deleteTemplate"
      @close="deleteTemplate = undefined"
      @deleted="onTemplateDeleted"
    />
    <LazyRemunerationNightHoursSettingsModal
      v-if="showNightHoursModal"
      :company-id="company.id"
      :settings="remunerationSettings"
      @close="showNightHoursModal = false"
      @updated="onSettingsUpdated"
    />
  </div>
</template>

<style scoped lang="scss">
.company-details-section-title {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 16px;
  font-weight: 900;
}

.night-hours-section {
  margin-top: 24px;
}
</style>
